<template>
  <div v-if="inout && inout.sts !== 'N'"
       class="inout-overlay"
       :class="{ 'inout-overlay-dark': $store.state.darkMode }">

    <table class="table table-sm table-borderless mb-0 inout-table">
      <!-- 진척 그래프 -->
      <tr>
        <td class="io-label">진척</td>
        <td>
          <CProgress :max="100" style="height:1.4rem" show-value>
            <CProgressBar :value="volPercent"
                          :animated="true"
                          :color="progressVariant(volPercent)"/>
          </CProgress>
        </td>
      </tr>

      <!-- 입출고량. 거래·정산 기준인 환산량(net)을 앞세우고 실측량(gross)을 붙인다 -->
      <tr>
        <td class="io-label">{{ ioLabel }}</td>
        <td class="text-right"
             v-b-tooltip.hover
             :title="`환산 ${trans(diffOvc,'v',volUnit)} (net, 정산기준) / 실측 ${trans(diffOvm,'v',volUnit)} (gross)`">
          <strong class="io-net" :class="{blink: true}">{{ trans(diffOvc, 'v', volUnit) }}</strong>
          <small class="io-gross"> / {{ trans(diffOvm, 'v', volUnit) }}</small>
          <CBadge color="secondary" class="ml-1">{{ tankUnitSign(volUnit) }}</CBadge>
        </td>
      </tr>

      <!--
        목표량 설정. 라벨 칼럼에 폭을 뺏기면 입력칸의 숫자가 잘리므로
        colspan 으로 한 행을 다 쓰고 라벨은 입력그룹 앞에 붙인다.
        칼럼 밖으로 넘기지 않아 우측 상세표와 겹치지 않는다.
      -->
      <tr v-if="showTarget">
        <td colspan="2" class="pt-0">
          <div class="input-group input-group-sm">
            <div class="input-group-prepend">
              <span class="input-group-text io-target-label">목표</span>
            </div>
            <input type="number" class="form-control" :placeholder="placeholder"
                   :value="targetVol"
                   @input="$emit('update:targetVol', $event.target.value)"/>
            <div class="input-group-append">
              <CButton color="success" @click="$emit('save')" title="목표량 저장"><BIconSave2/></CButton>
              <CButton color="danger" @click="$emit('clear')" title="작업 취소"><BIconTrash2/></CButton>
            </div>
          </div>
        </td>
      </tr>

      <!-- 호출측 전용 행(예: 급유차 인증번호) -->
      <slot name="append"/>
    </table>
  </div>
</template>

<style scoped>
/*
  탱크 캔버스 위에 겹쳐 띄운다. 부모가 position:relative 인 요소로 감싸야 한다.
  뒤의 탱크가 비치도록 배경을 반투명으로 둔다.
*/
/*
  탱크 위에 반투명하게 겹쳐 띄운다.
  흐름에 두면 오버레이 유무에 따라 탱크가 위아래로 움직여 카드마다
  높이가 달라 보인다. 절대배치라 탱크는 항상 칼럼 한가운데 고정된다.
  칼럼을 벗어나지 않으므로 우측 상세표와는 겹치지 않는다.
*/
.inout-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  padding: 0.2rem 0.25rem;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 0.25rem;
  background-color: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(1px);
}

.inout-overlay-dark {
  border-color: rgba(255, 255, 255, 0.2);
  background-color: rgba(24, 28, 33, 0.68);
  color: #e6e6e6;
}

/*
  값이 길어지면(환산/실측 두 수를 같이 보여준다) 표가 오버레이 폭을 넘어섰다.
  폭을 고정하고 넘치는 값은 줄바꿈한다.
*/
.inout-table {
  width: 100%;
  table-layout: fixed;
}

.inout-table >>> td {
  padding: 0.15rem 0.2rem;
  vertical-align: middle;
  border: none;
  overflow-wrap: anywhere;
}

/* append 슬롯 내용은 부모 스코프로 컴파일되므로 깊은 선택자로 함께 적용한다 */
.inout-table >>> .io-label {
  width: 32px;
  font-size: 0.75rem;
  opacity: 0.75;
  white-space: nowrap;
}

/* 입력그룹 안의 목표 라벨. 표 라벨 칼럼과 같은 크기감으로 맞춘다 */
.inout-table >>> .io-target-label {
  padding: 0 0.4rem;
  font-size: 0.75rem;
}

.io-net {
  font-size: 1rem;
}

.io-gross {
  opacity: 0.65;
}

@keyframes blink-effect {
  50% {
    opacity: 0;
  }
}

.blink {
  animation: blink-effect 1s step-end infinite;
}
</style>

<script>
/**
 * 입출고 진척 표시 (진척 그래프 + 누적 입출고량 + 목표량 설정)
 *
 * Tank2D / Tank3D / Tank3DV2 에 같은 블록이 복사돼 있던 것을 하나로 모은 것이다.
 * 한쪽만 고치는 일이 실제로 있었다(자동 시작 입고에서 목표량이 0 이라
 * 진척률이 Infinity 가 되던 문제를 Tank3DV2 에서만 고쳤다).
 *
 * 탱크 캔버스 위에 겹쳐 띄우므로 호출측은 position:relative 인 요소로 감싸야 한다.
 * 입출고 작업이 없으면(sts==='N') 아무것도 그리지 않아 캔버스를 가리지 않는다.
 *
 * 컴포넌트마다 달랐던 부분은 props 로 받는다.
 *   - 입력칸 문구  : Tank2D 는 '목표량...입력', 나머지는 '기준량... 설정'
 *   - 입력칸 노출  : Tank2D 는 급유차 형태(tankShape==='4')에서 숨긴다
 *   - 뒤쪽 추가행  : Tank2D 의 인증번호 표시는 append 슬롯으로 받는다
 */
import {trans, tankUnitSign} from '@/common/utils';
import {IoStsMap} from '@/common/constants';

export default {
  name: 'InoutProgress',
  props: {
    // 입출고 정보. sts 가 'N' 이면 아무것도 그리지 않는다.
    inout: {type: Object, default: null},
    // 진척률(%)
    volPercent: {type: Number, default: 0},
    // 시작 대비 누적 변화량(리터). 표시 단위 환산은 이 컴포넌트가 한다.
    // diffOvm 은 실측(gross), diffOvc 는 온도환산(net)이다.
    // 경보·진척률은 실제 부피로 판정하므로 gross 기준이지만,
    // 거래·정산 기준은 net 이므로 화면에는 net 을 앞세운다.
    diffOvm: {type: Number, default: 0},
    diffOvc: {type: Number, default: 0},
    // 표시 부피단위 ('l' | 'g')
    volUnit: {type: String, default: 'l'},
    // 목표량 입력값 (.sync 로 연결)
    targetVol: {type: [Number, String], default: null},
    // 목표량 입력칸과 저장/삭제 버튼 노출 여부
    showTarget: {type: Boolean, default: true},
    // 입력칸 placeholder. 미지정 시 '기준량(단위) 설정'
    placeholderText: {type: String, default: null},
  },
  computed: {
    placeholder(){
      return this.placeholderText !== null
        ? this.placeholderText
        : `기준량(${tankUnitSign(this.volUnit)}) 설정`;
    },

    // 작업 종류에 맞는 수량 이름. 상태를 모르면 '수량' 으로 둔다.
    ioLabel(){
      const sts = this.inout && this.inout.sts;
      const name = IoStsMap[sts];
      return name ? `${name}량` : '수량';
    },
  },
  methods: {
    trans,
    tankUnitSign,

    progressVariant(v){
      if(!v) return 'dark';

      if(v<10) return 'info';
      else if(v < 40) return 'primary';
      else if(v < 80) return 'success';
      else if(v < 95) return 'warning';
      else if(v >= 95) return 'danger';
      else return 'info';
    },
  },
}
</script>